<script lang="ts" setup>
import { computed, getCurrentInstance, ref } from 'vue';
import { finalizeConfirmRequestApi } from '@Api/requestFlow.api';
import type { SccSubmitResult } from '@Types/SccSubmitResult.interface';
import type { Address } from '@Types/WiringInstrucions.interface';
import ConfirmModal from '@Components/modals/shared/ConfirmModal.vue';
import type { ConfirmFooterButton } from '@Components/modals/shared/ConfirmModal.vue';

const props = defineProps<{
    modelValue: boolean;
    personalAddress?: Address | null;
}>();

const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void;
    // PLAT-2857: the finalize result rides on the event so the view can route
    // blocked outcomes or open the optional Plaid upgrade.
    (e: 'approved', result: SccSubmitResult): void;
}>();

const instance = getCurrentInstance();
const proxy = instance?.proxy as { $apptoast: (msg: string, type: string) => void } | undefined;

const isSubmitting = ref(false);

const isModalOpen = computed({
    get: () => props.modelValue,
    set: (value: boolean) => emit('update:modelValue', value)
});

const closeModal = () => {
    isModalOpen.value = false;
};

const approve = async () => {
    isSubmitting.value = true;
    try {
        const result = await finalizeConfirmRequestApi(true, null, null, props.personalAddress ?? null);
        // Close first: on an open_plaid outcome the view stays on this page and
        // opens the Plaid modal instead of navigating away.
        isModalOpen.value = false;
        emit('approved', result);
    } catch {
        proxy?.$apptoast('Something went wrong. Please try again.', 'error');
    } finally {
        isSubmitting.value = false;
    }
};

const footerButtons = computed<ConfirmFooterButton[]>(() => [
    {
        type: 'outline',
        text: 'No',
        dataId: 'confirm-approve-no',
        isDisabled: isSubmitting.value,
        onClick: closeModal
    },
    {
        type: 'primary',
        text: 'Yes',
        dataId: 'confirm-approve-yes',
        isLoading: isSubmitting.value,
        onClick: approve
    }
]);
</script>

<template>
    <confirm-modal
        v-model="isModalOpen"
        icon="cd-ic-info-circle"
        icon-style="default"
        header="Are these account details correct?"
        sub-header="By approving, you are authorizing the use of these account details to receive funds."
        :footer-buttons="footerButtons" 
    />
</template>
